import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Dialog, Transition } from "@headlessui/react";
import { Disclosure, Menu, Popover } from "@headlessui/react";
import { Fragment, useState } from "react";
import { useRouter } from "next/router";
import { XMarkIcon as XIcon } from "@heroicons/react/24/outline";
import Link from "next/link";

import {
  getAllPostsForBlog,
  getAllCategories,
  getSettingsForPage,
} from "../lib/api";
import BlogCard from "../components/blog-card";
import Meta from "../components/meta";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function NextLink(props) {
  let { href, children, ...rest } = props;
  return (
    <Link href={href} shallow scroll={false} {...rest}>
      {children}
    </Link>
  );
}

const filterData = (data, categories, sort) => {
  if (!data || !data.length) return [];

  data = [...data];

  if (categories.length) {
    data = data.filter((i) => {
      const categoriesLookup = categories.map((i) => i.value);
      const cats = i.categories;
      if (!cats || !cats.length) return false;
      return (
        cats.findIndex((cat) =>
          categoriesLookup.find((i) => cat.slug.current === i)
        ) > -1
      );
    });
  }

  if (sort) {
    if (sort === "title") {
      data.sort((a, b) => {
        if (a.title < b.title) return -1;
        if (a.title > b.title) return 1;
        return 0;
      });
    }
  }

  return data;
};

export default function Blog({ data: initialData = [], categories, settings }) {
  const router = useRouter();
  const { query } = router || {};
  const { categories: cats = "", sort } = query || {};

  let activeFilters = [];
  if (cats) {
    activeFilters = cats.split(",").map((value) => ({
      value,
      label: categories.find((i) => i.slug.current === value).title,
    }));
  }

  let activeFilterString = "";
  if (cats) {
    activeFilterString = `?categories=${cats}`;
  }
  const filtersActive = activeFilters && activeFilters.length;

  const sortOptions = [
    {
      name: "Newest",
      href: filtersActive ? `/blog${activeFilterString}` : "/blog",
      current: !sort,
    },
    {
      name: "Title",
      href: filtersActive
        ? `/blog${activeFilterString}&sort=title`
        : "/blog?sort=title",
      current: sort === "title",
    },
  ];

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const filters = [
    {
      id: "category",
      name: "Category",
      options: categories.map((i) => ({
        value: i.slug.current,
        label: i.title,
        checked: activeFilters.find((x) => x.value === i.slug.current),
      })),
    },
  ];
  const data = filterData(initialData, activeFilters, sort);

  const handleFilterChanged = (e) => {
    const { value, checked, dataset } = e.target;
    const { id: type } = dataset || {};

    let categoriesQueryParamsStr = "";
    const categoriesArr = cats ? cats.split(",") : [];
    if (type === "category") {
      if (!checked && categoriesArr.includes(value)) {
        const index = categoriesArr.findIndex((i) => i === value);
        categoriesArr.splice(index, 1);
      } else if (checked && !categoriesArr.includes(value)) {
        categoriesArr.push(value);
      }
    }
    categoriesQueryParamsStr = categoriesArr.join(",");

    let queryParamsStr = "";
    if (categoriesQueryParamsStr) {
      queryParamsStr = `?categories=${categoriesQueryParamsStr}`;
    }
    if (sort) {
      const symbol = queryParamsStr ? "&" : "?";
      queryParamsStr = `${queryParamsStr}${symbol}sort=${sort}`;
    }

    const vw = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0
    );
    const isMobile = vw <= 1024;
    router.push(`/blog${queryParamsStr}`, undefined, {
      scroll: isMobile,
      shallow: true,
    });
  };

  return (
    <div className="bg-gray-50">
      <Meta path="/blog" {...settings.seo} />

      <div>
        {/* Mobile filter dialog */}
        <Transition.Root show={mobileFiltersOpen} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 flex z-40 sm:hidden"
            onClose={setMobileFiltersOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="ml-auto relative max-w-xs w-full h-full bg-white shadow-xl py-4 pb-12 flex flex-col overflow-y-auto">
                <div className="px-4 flex items-center justify-between">
                  <h2 className="text-lg font-medium text-gray-900">Filters</h2>
                  <button
                    type="button"
                    className="-mr-2 w-10 h-10 bg-white p-2 rounded-md flex items-center justify-center text-gray-400"
                    onClick={() => setMobileFiltersOpen(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {/* Filters */}
                <form className="mt-4">
                  {filters.map((section) => (
                    <Disclosure
                      as="div"
                      key={section.name}
                      className="border-t border-gray-200 px-4 py-6"
                    >
                      {({ open }) => (
                        <>
                          <h3 className="-mx-2 -my-3 flow-root">
                            <Disclosure.Button className="px-2 py-3 bg-white w-full flex items-center justify-between text-sm text-gray-400">
                              <span className="font-medium text-gray-900">
                                {section.name}
                              </span>
                              <span className="ml-6 flex items-center">
                                <ChevronDownIcon
                                  className={classNames(
                                    open ? "-rotate-180" : "rotate-0",
                                    "h-5 w-5 transform"
                                  )}
                                  aria-hidden="true"
                                />
                              </span>
                            </Disclosure.Button>
                          </h3>
                          <Disclosure.Panel className="pt-6">
                            <div className="space-y-6">
                              {section.options.map((option, optionIdx) => (
                                <div
                                  key={option.value}
                                  className="flex items-center"
                                >
                                  <input
                                    id={`filter-mobile-${section.id}-${optionIdx}`}
                                    name={`${section.id}[]`}
                                    defaultValue={option.value}
                                    type="checkbox"
                                    defaultChecked={option.checked}
                                    data-id={section.id}
                                    onInput={handleFilterChanged}
                                    className="h-4 w-4 border-gray-300 rounded text-accent-600 focus:ring-accent-500"
                                  />
                                  <label
                                    htmlFor={`filter-mobile-${section.id}-${optionIdx}`}
                                    className="ml-3 text-sm text-gray-500"
                                  >
                                    {option.label}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  ))}
                </form>
              </div>
            </Transition.Child>
          </Dialog>
        </Transition.Root>

        <main>
          <div className="bg-white">
            <div className="max-w-7xl mx-auto pt-10 px-4 sm:px-6 lg:px-8 pb-6">
              <h1 className="text-4xl font-extrabold tracking-tight text-gray-900">
                Blog
              </h1>
            </div>
          </div>

          {/* Filters */}
          <section aria-labelledby="filter-heading">
            <h2 id="filter-heading" className="sr-only">
              Filters
            </h2>

            <div className="relative z-10 bg-white border-b border-gray-200 pb-4">
              <div className="max-w-7xl mx-auto px-4 flex items-center justify-between sm:px-6 lg:px-8">
                <Menu as="div" className="relative inline-block text-left">
                  <div>
                    <Menu.Button className="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                      Sort
                      <ChevronDownIcon
                        className="flex-shrink-0 -mr-1 ml-1 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                    </Menu.Button>
                  </div>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="origin-top-left absolute left-0 mt-2 w-40 rounded-md shadow-2xl bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="py-1">
                        {sortOptions.map((option) => (
                          <Menu.Item key={option.name}>
                            {({ active }) => (
                              <NextLink
                                href={option.href}
                                className={classNames(
                                  option.current
                                    ? "font-medium text-gray-900"
                                    : "text-gray-500",
                                  active ? "bg-gray-100" : "",
                                  "block px-4 py-2 text-sm"
                                )}
                              >
                                {option.name}
                              </NextLink>
                            )}
                          </Menu.Item>
                        ))}
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>

                <button
                  type="button"
                  className="inline-block text-sm font-medium text-gray-700 hover:text-gray-900 sm:hidden"
                  onClick={() => setMobileFiltersOpen(true)}
                >
                  Filters
                </button>

                <div className="hidden sm:block">
                  <div className="flow-root">
                    <Popover.Group className="-mx-4 flex items-center divide-x divide-gray-200">
                      {filters.map((section) => (
                        <Popover
                          key={section.name}
                          className="px-4 relative inline-block text-left"
                        >
                          <Popover.Button className="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                            <span>{section.name}</span>
                            {activeFilters && activeFilters.length ? (
                              <span className="ml-1.5 rounded py-0.5 px-1.5 bg-gray-200 text-xs font-semibold text-gray-700 tabular-nums">
                                {activeFilters.length}
                              </span>
                            ) : null}

                            <ChevronDownIcon
                              className="flex-shrink-0 -mr-1 ml-1 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                              aria-hidden="true"
                            />
                          </Popover.Button>

                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Popover.Panel className="origin-top-right absolute right-0 mt-2 bg-white rounded-md shadow-2xl p-4 ring-1 ring-black ring-opacity-5 focus:outline-none">
                              <form className="space-y-4">
                                {section.options.map((option, optionIdx) => (
                                  <div
                                    key={option.value}
                                    className="flex items-center"
                                  >
                                    <input
                                      id={`filter-${section.id}-${optionIdx}`}
                                      data-id={section.id}
                                      name={`${section.id}[]`}
                                      defaultValue={option.value}
                                      type="checkbox"
                                      defaultChecked={option.checked}
                                      onInput={handleFilterChanged}
                                      className="h-4 w-4 border-gray-300 rounded text-accent-600 focus:ring-accent-500"
                                    />
                                    <label
                                      htmlFor={`filter-${section.id}-${optionIdx}`}
                                      className="ml-3 pr-6 text-sm font-medium text-gray-900 whitespace-nowrap"
                                    >
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </form>
                            </Popover.Panel>
                          </Transition>
                        </Popover>
                      ))}
                    </Popover.Group>
                  </div>
                </div>
              </div>
            </div>

            {/* Active filters */}
            {activeFilters && activeFilters.length ? (
              <div className="bg-gray-100">
                <div className="max-w-7xl mx-auto py-3 px-4 sm:flex sm:items-center sm:px-6 lg:px-8">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Filters
                    <span className="sr-only">, active</span>
                  </h3>

                  <div
                    aria-hidden="true"
                    className="hidden w-px h-5 bg-gray-300 sm:block sm:ml-4"
                  />

                  <div className="mt-2 sm:mt-0 sm:ml-4">
                    <div className="-m-1 flex flex-wrap items-center">
                      {activeFilters.map((activeFilter) => (
                        <span
                          key={activeFilter.value}
                          className="m-1 inline-flex rounded-full border border-gray-200 items-center py-1.5 pl-3 pr-2 text-sm font-medium bg-white text-gray-900"
                        >
                          <span>{activeFilter.label}</span>
                          <button
                            type="button"
                            onClick={() => {
                              handleFilterChanged({
                                target: {
                                  value: activeFilter.value,
                                  checked: false,
                                  dataset: { id: "category" },
                                },
                              });
                            }}
                            className="flex-shrink-0 ml-1 h-4 w-4 p-1 rounded-full inline-flex text-gray-400 hover:bg-gray-200 hover:text-gray-500"
                          >
                            <span className="sr-only">
                              Remove filter for {activeFilter.label}
                            </span>
                            <svg
                              className="h-2 w-2"
                              stroke="currentColor"
                              fill="none"
                              viewBox="0 0 8 8"
                            >
                              <path
                                strokeLinecap="round"
                                strokeWidth="1.5"
                                d="M1 1l6 6m0-6L1 7"
                              />
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </section>

          {/* Product grid */}
          <section
            aria-labelledby="products-heading"
            className="max-w-2xl mx-auto pt-12 pb-16 px-4 sm:pb-24 sm:px-6 lg:max-w-7xl lg:px-8"
          >
            <h2 id="products-heading" className="sr-only">
              Products
            </h2>

            <div className="grid grid-cols-1 gap-y-10 sm:grid-cols-2 gap-x-6 lg:grid-cols-3">
              {data.map((post) => (
                <BlogCard key={post["_id"]} post={post} />
              ))}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

export async function getStaticProps({ preview = false }) {
  const data = await getAllPostsForBlog(preview);
  const categories = await getAllCategories(preview);
  const settings = await getSettingsForPage("blog");

  return {
    props: {
      data,
      categories,
      settings,
    },
    revalidate: 10,
  };
}
